<script setup lang="ts">
// Ports OBadge/OInfoBadge — `tone` replaces the legacy bg/ring/color pass-through props.
import type { BadgeProps } from './types'

withDefaults(defineProps<BadgeProps>(), {
  tone: 'neutral',
  size: 'md',
  shape: 'pill',
  strong: false,
  removable: false,
  removeLabel: 'common.clear',
})

const emit = defineEmits<{ remove: [] }>()

const toneClasses = {
  neutral: 'bg-surface-sunken text-muted',
  info: 'bg-info-soft text-info',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
  // No colour at all: the host owns it (see `BadgeProps.tone`).
  plain: '',
} as const

const sizeClasses = {
  sm: 'px-[0.45rem] py-[0.05rem] text-[0.6875rem]',
  md: 'px-[0.6rem] py-[0.15rem] text-xs',
  lg: 'px-2 py-1 text-sm',
} as const

const shapeClasses = {
  pill: 'rounded-full',
  rounded: 'rounded-sm',
} as const
</script>

<template>
  <span
    class="ui-badge inline-flex items-center gap-2xs font-sans font-medium whitespace-nowrap"
    :class="[
      `ui-badge--${tone}`,
      `ui-badge--${size}`,
      `ui-badge--${shape}`,
      toneClasses[tone],
      sizeClasses[size],
      shapeClasses[shape],
      { 'ui-badge--strong font-bold': strong },
    ]"
  >
    <slot />
    <button
      v-if="removable"
      type="button"
      class="ui-badge__remove inline-flex border-0 bg-transparent text-current opacity-70 text-[0.7em] hover:opacity-100"
      :aria-label="removeLabel"
      @click="emit('remove')"
    >
      ✕
    </button>
  </span>
</template>
